import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Id } from '@cvx/_generated/dataModel'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@rekrytering/ui/components/select'
import { useMutation, useQuery } from '@tanstack/react-query'

type Props = {
  readonly jobId: Id<'jobs'>
}

export const JobTagsEditor = ({ jobId }: Props) => {
  const { data: allTags = [] } = useQuery(
    convexQuery(api.features.candidates.tags.listMyTags, {}),
  )

  const { data: jobTags = [] } = useQuery(
    convexQuery(api.features.candidates.tags.listTagsForMyJob, { jobId }),
  )

  const { mutateAsync: addTag } = useMutation({
    mutationFn: useConvexMutation(api.features.candidates.tags.addTagToMyJob),
  })

  const { mutateAsync: removeTag } = useMutation({
    mutationFn: useConvexMutation(
      api.features.candidates.tags.removeTagFromMyJob,
    ),
  })

  const assignedTagIds = new Set(jobTags.map((t) => t._id))
  const availableTags = allTags.filter((t) => !assignedTagIds.has(t._id))

  if (allTags.length === 0) return null

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {jobTags.map((tag) => (
        <span
          key={tag._id}
          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
          style={{ backgroundColor: `${tag.color}20`, color: tag.color }}
        >
          {tag.label}
          <button
            type="button"
            className="hover:opacity-70"
            onClick={(e) => {
              e.stopPropagation()
              void removeTag({ jobId, tagId: tag._id as Id<'tags'> })
            }}
          >
            ×
          </button>
        </span>
      ))}
      {availableTags.length > 0 && (
        <Select
          value=""
          onValueChange={(value) =>
            void addTag({ jobId, tagId: value as Id<'tags'> })
          }
        >
          <SelectTrigger className="h-6 w-24 text-xs">
            <SelectValue placeholder="+ Tag" />
          </SelectTrigger>
          <SelectContent>
            {availableTags.map((tag) => (
              <SelectItem key={tag._id} value={tag._id}>
                <span
                  className="inline-block rounded-full px-2 py-0.5 text-xs font-medium"
                  style={{
                    backgroundColor: `${tag.color}20`,
                    color: tag.color,
                  }}
                >
                  {tag.label}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  )
}
